import { DefaultTheme, styled } from 'styled-components';
import { InformationType, Size } from '../../types';
import { getColorByInfoType, InformationMessageProps } from '../input.styles';

type InformationMessage = InformationMessageProps;

export const ICON_SIZE = 16;

interface ContainerProps {
    $disabled?: boolean;
    $slim?: boolean;
    $size?: Size;
    $readOnly: boolean;
    $hasLabel: boolean;
}

function getColorIfError(
    theme: DefaultTheme,
    hasMessage: boolean,
    informationType: InformationType,
    defaultColor: string,
    disabled?: boolean,
    invalid?: boolean
): string {
    if (disabled) {
        return theme.foreground.disabled ?? '#999';
    }
    return invalid || (hasMessage && informationType === InformationType.ERROR)
        ? theme.inputMessageColorError
        : defaultColor;
}

function getContainerBackgroundColor(
    theme: DefaultTheme,
    hasMessage: boolean,
    informationType: InformationType,
    readOnly: boolean,
    invalid?: boolean
) {
    if (readOnly) {
        return theme.inputBorderReadOnly;
    }
    if (invalid) {
        return `${theme.inputMessageBorder} ${theme.inputMessageColorError}`;
    }
    if (hasMessage) {
        return `${theme.inputMessageBorder} ${getColorByInfoType(theme, informationType)}`;
    }
    return theme.inputBorder;
}

/**
 * The focused border, which has to keep an error visible.
 *
 * <p>Focus otherwise paints over it, so a field would lose its outline exactly while it is being
 * corrected. Mirrors getContainerBorderColorFocused in dropdown.styles.ts.
 */
function getContainerBorderColorFocused(
    theme: DefaultTheme,
    hasMessage: boolean,
    informationType: InformationType,
    readOnly: boolean,
    invalid?: boolean
) {
    if (readOnly) {
        return theme.inputBorderReadOnlyActive;
    }
    if (invalid || (hasMessage && informationType === InformationType.ERROR)) {
        return `${theme.inputMessageBorder} ${theme.inputMessageColorError}`;
    }
    return theme.inputBorderActive;
}

export const Container = styled.div<ContainerProps & InformationMessage>`
    min-width: 0;
    display: flex;
    flex-direction: column;
    /* When a label is present, pin Content to the bottom so the
       absolutely-positioned Label has clear space at the top when it's in
       its active (small-at-top) state. Otherwise just center Content. */
    justify-content: ${({ $slim, $hasLabel }) => ($slim || !$hasLabel ? 'center' : 'flex-end')};
    align-items: flex-start;
    background: ${({ theme, $readOnly }) =>
        $readOnly ? theme.inputReadOnlyBackground : theme.inputBackground};
    border-radius: ${({ theme, $size = Size.NORMAL }) =>
        theme.componentSizes.inputField[$size].radius}px;
    padding: ${({ theme, $size = Size.NORMAL }) => {
        const s = theme.componentSizes.inputField[$size];
        return `${s.verticalPadding}px ${s.horizontalPadding}px`;
    }};
    position: relative;
    height: ${({ theme, $size = Size.NORMAL }) => theme.componentSizes.inputField[$size].height}px;
    box-sizing: border-box;

    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'inherit')};

    &:after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        border-radius: ${({ theme, $size = Size.NORMAL }) =>
            theme.componentSizes.inputField[$size].radius}px;
        border: ${({ theme, $hasMessage, $informationType, $readOnly, $invalid }) =>
            getContainerBackgroundColor(theme, $hasMessage, $informationType, $readOnly, $invalid)};
        pointer-events: none;
    }

    &:focus-within {
        background: ${({ theme, $readOnly }) =>
            $readOnly ? theme.inputReadOnlyActiveBackground : theme.inputActiveBackground};
        &:after {
            border: ${({ theme, $readOnly, $hasMessage, $informationType, $invalid }) =>
                getContainerBorderColorFocused(
                    theme,
                    $hasMessage,
                    $informationType,
                    $readOnly,
                    $invalid
                )};
        }
    }
`;

export const Input = styled.input<
    { $hasIcon: boolean; $size?: Size; $hasLabel: boolean } & InformationMessage
>`
    flex: 1;
    border: none;
    appearance: none;
    /* Fixed font-size across all Sizes — Size controls density, not text scale.
       Uses the 'description' typography token (brand-aware). */
    font-size: ${({ theme }) => theme.typography.fontSize.description}px;
    /* Prevent iOS Safari from auto-zooming on focus when font-size < 16px. */
    @media (max-width: 600px) {
        font-size: 16px;
    }
    font-family: ${({ theme }) => theme.typography.secondaryFontFamily};
    color: ${({ theme, $hasMessage, $informationType, disabled, $invalid }) =>
        getColorIfError(
            theme,
            $hasMessage,
            $informationType,
            theme.inputInputColor,
            disabled,
            $invalid
        )};
    font-weight: 400;
    padding: 0;
    outline: none;
    background-color: transparent !important;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'inherit')};
    margin-left: ${({ $hasIcon, theme }) =>
        $hasIcon ? `calc(${ICON_SIZE}px + ${theme.spacing(2)})` : 0};
    width: 100%;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    &&[type='number'] {
        -moz-appearance: textfield;
    }
    &[type='time'] {
        text-align: left;
        min-height: 1em;
    }
    &[type='time']::-webkit-date-and-time-value {
        text-align: left;
    }
`;

export const Label = styled.label<
    {
        $hasIcon: boolean;
        $disabled?: boolean;
        $size?: Size;
    } & InformationMessage
>`
    display: block;
    position: absolute;
    z-index: 1;
    /* Left-align with the input text (account for icon offset). */
    left: ${({ $hasIcon, theme, $size = Size.NORMAL }) => {
        const pad = theme.componentSizes.inputField[$size].horizontalPadding;
        return $hasIcon ? `calc(${pad}px + ${ICON_SIZE}px + ${theme.spacing(2)})` : `${pad}px`;
    }};
    right: ${({ theme }) => theme.spacing(4)};
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme, $hasMessage, $informationType, $disabled, $invalid }) =>
        $disabled
            ? (theme.foreground.disabled ?? '#999')
            : getColorIfError(
                  theme,
                  $hasMessage,
                  $informationType,
                  theme.inputLabelColor,
                  undefined,
                  $invalid
              )};
    font-family: ${({ theme }) => theme.typography.secondaryFontFamily};
    font-weight: 400;
    pointer-events: none;
    transition: all 0.2s ease-in-out;

    /* Inactive (empty + unfocused): vertically centered, larger — acts as placeholder. */
    top: 50%;
    transform: translateY(-50%);
    font-size: ${({ theme }) => theme.typography.fontSize.description}px;
    line-height: normal;

    /* Active (focused, has value, has a visible placeholder, or $alwaysFloat): small at the top. */
    ${Container}:focus-within > &,
    ${Container}:has(${Input}:not([value=''])) > &,
    ${Container}:has(${Input}[placeholder]:not([placeholder=''])) > &,
    &[data-always-float='true'] {
        top: ${({ theme, $size = Size.NORMAL }) =>
            theme.componentSizes.inputField[$size].verticalPadding}px;
        transform: translateY(0);
        font-size: ${({ theme }) => theme.typography.fontSize.descriptionSmall}px;
        line-height: 1.3;
    }
`;

/* Slim mode hides the label entirely when the input is active or when a
   placeholder is visible (so the slim label doesn't overlap placeholder text). */
export const SlimLabel = styled(Label)`
    ${Container}:focus-within > &,
    ${Container}:has(${Input}:not([value=''])) > &,
    ${Container}:has(${Input}[placeholder]:not([placeholder=''])) > &,
    &[data-always-float='true'] {
        display: none;
    }
`;

export const CurrencySymbol = styled.span<{ visible: boolean; $size?: Size }>`
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    color: ${({ theme }) => theme.inputLabelColor};
    font-family: ${({ theme }) => theme.typography.secondaryFontFamily};
    font-size: ${({ theme }) => theme.typography.fontSize.description}px;
    /* Match Input: avoid iOS Safari auto-zoom when the field is focused. */
    @media (max-width: 600px) {
        font-size: 16px;
    }
    white-space: nowrap;
`;

export const Unit = styled.span<{ visible: boolean }>`
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    color: ${({ theme }) => theme.inputLabelColor};
    white-space: nowrap;
`;

export const Wrapper = styled.div<{ $flex?: string }>`
    display: flex;
    flex-direction: column;
    flex: ${({ $flex }) => $flex ?? '1'};
    min-width: 0;
`;

export const Content = styled.div<{ $size?: Size }>`
    display: flex;
    align-items: center;
    gap: ${({ theme, $size }) => ($size === Size.TINY ? theme.spacing(0.5) : theme.spacing(1))};
    width: 100%;
`;

export const IconWrapper = styled.span<{ $size?: Size }>`
    position: absolute;
    left: ${({ theme, $size = Size.NORMAL }) =>
        theme.componentSizes.inputField[$size].horizontalPadding}px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    pointer-events: none;
`;

export const ReadOnlyIcon = styled.span<{ $size?: Size }>`
    position: absolute;
    right: ${({ theme }) => theme.spacing(5)};
    top: ${({ theme, $size }) =>
        $size === Size.TINY || $size === Size.SMALL ? theme.spacing(4) : theme.spacing(5)};
`;

export const HelpIcon = styled.button<{ $size?: Size }>`
    /* reset + hit area */
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin: 0;
    border: 0;
    height: 100%;
    background: transparent;
    border-radius: ${({ theme }) => theme.spacing(1)};

    color: ${({ theme }) => theme.inputLabelColor};

    &:hover {
        color: ${({ theme }) => theme.inputInputColor};
    }

    &:focus-visible {
        outline: none;
        /* fallback-friendly focus ring */
        box-shadow: 0 0 0 2px ${({ theme }) => theme.focusColor};
    }

    /* keep tooltips clickable even if container has pointer-events: none */
    pointer-events: auto;
`;
